import { Panel, PanelAnchor } from '@insify/design-system/components';
import { textSmSemibold } from '@insify/design-system/typography';
import { capitalize } from '@insify/ui-utils';
import groupBy from 'lodash/groupBy';
import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import * as styles from './ExclusionsOverview.module.css';

import type { RichContentType } from '../../../components/RichContent';
import type { Exclusion, ExclusionSegmentSpecific } from '../Exclusions';

type ExclusionIcons<ExclusionType extends string, CoverageType extends string> = {
  [Icon in ExclusionType | CoverageType]: React.ReactElement;
};

type ExclusionTranslations<ExclusionType extends string, CoverageType extends string> = {
  [T in CoverageType as `accordion${Capitalize<T>}Header`]?: string;
} & {
  [T in ExclusionType as `accordion${Capitalize<T>}Description`]?: React.ReactNode;
} & {
  // Toggle link text
  [T in CoverageType as `accordion${Capitalize<T>}Toggle`]?: string;
} & {
  // Expandable details header
  [T in CoverageType as `accordion${Capitalize<T>}DetailsHeader`]?: string;
};

export type ExclusionsContentProps<ExclusionType extends string, CoverageType extends string> = {
  content?: RichContentType;
  icons: ExclusionIcons<ExclusionType, CoverageType>;
  translations: {
    back: string;
    submit: string;
  } & ExclusionTranslations<ExclusionType, CoverageType>;
};

export type ExclusionsOverviewProps<ExclusionType extends string, CoverageType extends string> = ExclusionsContentProps<
  ExclusionType,
  CoverageType
> & {
  /** Providing these will make sure you need to add the translations / icons for each exclusion type */
  exclusions?: Exclusion<ExclusionType>[];
  /** Providing these will make sure you need to add the translations / icons for each exclusion coverage */
  exclusionsSegmentSpecific?: ExclusionSegmentSpecific<CoverageType>[];
  exclusionsSegmentSpecificOrder?: CoverageType[];
};

export const ExclusionsOverview = <ExclusionType extends string, CoverageType extends string>({
  exclusions,
  exclusionsSegmentSpecific,
  exclusionsSegmentSpecificOrder,
  icons,
  translations,
}: ExclusionsOverviewProps<ExclusionType, CoverageType>) => {
  const exclusionsGroupedByCoverage = useMemo(() => {
    const grouped = groupBy(exclusionsSegmentSpecific, 'coverage');
    return exclusionsSegmentSpecificOrder
      ?.map((item) => ({
        coverage: item,
        exclusions: grouped[item],
      }))
      .filter((item) => item.exclusions);
  }, [exclusionsSegmentSpecific, exclusionsSegmentSpecificOrder]);

  return (
    <>
      {exclusions?.map((exclusion) => (
        <Panel
          key={exclusion.type}
          id={exclusion.type}
          icon={icons[exclusion.type as keyof typeof icons]}
          description={
            translations[`accordion${capitalize(exclusion.type)}Description` as keyof typeof translations] as string
          }
          showToggle={false}
          trackingId={`${exclusion.type}-exclusion`}
          className={styles.exclusion}
        />
      ))}
      {exclusionsGroupedByCoverage &&
        exclusionsGroupedByCoverage.map(({ coverage, exclusions }) => {
          const innerTitle = translations[
            `accordion${capitalize(coverage)}DetailsHeader` as keyof typeof translations
          ] as string;
          const details = (
            <>
              {innerTitle && <div className={textSmSemibold}>{innerTitle}</div>}
              <ul className={styles.list}>
                {exclusions
                  ?.sort(({ order: orderA = 0 }, { order: orderB = 0 }) => orderA - orderB)
                  .filter(({ id }, index, list) => list.findIndex(({ id: otherId }) => otherId === id) === index)
                  .map((exclusion) => <li key={uuid()}>{exclusion.exclusion}</li>)}
              </ul>
            </>
          );
          return (
            <Panel
              id={coverage}
              key={coverage}
              icon={icons[coverage as keyof typeof icons]}
              header={translations[`accordion${capitalize(coverage)}Header` as keyof typeof translations] as string}
              description={
                <PanelAnchor data-testid={`toggle-${coverage}`}>
                  {translations[`accordion${capitalize(coverage)}Toggle` as keyof typeof translations] as string}
                </PanelAnchor>
              }
              details={details}
              showToggle={false}
              trackingId={`${coverage}-exclusion`}
              className={styles.exclusion}
            />
          );
        })}
    </>
  );
};
